import { useEffect, useState } from 'react';
import './App.css';
import catalogue from './catalogue.generated';

function App() {
  const phoneNumber = '212669580327';
  const [selectedSizes, setSelectedSizes] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const [catalogueInfo, setCatalogueInfo] = useState({});

  useEffect(() => {
    fetch('/catalogue-info.json')
        .then((response) => {
          if (!response.ok) {
            throw new Error('Impossible de charger catalogue-info.json');
          }

          return response.json();
        })
        .then((data) => {
          setCatalogueInfo(data);
        })
        .catch((error) => {
          console.error(error);
          setCatalogueInfo({});
        });
  }, []);

  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      'Bonjour Pouf Elite, je souhaite commander un pouf.'
  )}`;

  function getPanelKey(model, material) {
    return `${model.id}-${material.id}`;
  }

  function getSelectedSize(model, material) {
    const panelKey = getPanelKey(model, material);
    const selectedSizeId = selectedSizes[panelKey];

    return material.sizes.find((size) => size.id === selectedSizeId) || material.sizes[0];
  }

  function getColorSelectionKey(modelId, materialId, sizeId) {
    return `${modelId}-${materialId}-${sizeId}`;
  }

  function getSelectedColor(model, material, size) {
    const colorSelectionKey = getColorSelectionKey(model.id, material.id, size.id);
    const selectedColorId = selectedColors[colorSelectionKey];

    return size.colors.find((color) => color.id === selectedColorId) || size.colors[0];
  }

  function getSizeInfo(model, material, size) {
    return catalogueInfo?.[model.id]?.[material.id]?.[size.id] || {
      nom: model.name,
      matiere: material.name,
      taille: size.name,
      hauteur: '',
      largeur: '',
      prix: 0,
      promotion: 0,
    };
  }

  function getPromoPrice(price, promotion) {
    const numericPrice = Number(price);
    const numericPromotion = Number(promotion);

    if (!numericPrice || !numericPromotion) {
      return numericPrice;
    }

    return Math.round(numericPrice - (numericPrice * numericPromotion) / 100);
  }

  function createProductWhatsappLink(modelName, materialName, sizeName, colorName, sizeInfo) {
    const promoPrice = getPromoPrice(sizeInfo.prix, sizeInfo.promotion);

    const message = [
      'Bonjour Pouf Elite, je souhaite commander :',
      `Modèle : ${sizeInfo.nom || modelName}`,
      `Matière : ${sizeInfo.matiere || materialName}`,
      `Taille : ${sizeInfo.taille || sizeName}`,
      `Couleur : ${colorName}`,
      sizeInfo.hauteur ? `Hauteur : ${sizeInfo.hauteur}` : '',
      sizeInfo.largeur ? `Largeur : ${sizeInfo.largeur}` : '',
      sizeInfo.prix ? `Prix : ${sizeInfo.prix} DH` : '',
      sizeInfo.promotion ? `Promotion : ${sizeInfo.promotion}%` : '',
      promoPrice && sizeInfo.promotion ? `Prix après promotion : ${promoPrice} DH` : '',
    ]
        .filter(Boolean)
        .join('\n');

    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  }

  function selectSize(model, material, size) {
    const panelKey = getPanelKey(model, material);

    setSelectedSizes((currentSelectedSizes) => ({
      ...currentSelectedSizes,
      [panelKey]: size.id,
    }));

    const colorSelectionKey = getColorSelectionKey(model.id, material.id, size.id);

    setSelectedColors((currentSelectedColors) => ({
      ...currentSelectedColors,
      [colorSelectionKey]: currentSelectedColors[colorSelectionKey] || size.colors[0]?.id,
    }));
  }

  function selectColor(model, material, size, color) {
    const colorSelectionKey = getColorSelectionKey(model.id, material.id, size.id);

    setSelectedColors((currentSelectedColors) => ({
      ...currentSelectedColors,
      [colorSelectionKey]: color.id,
    }));
  }

  return (
      <div className="App">
        <header className="site-header">
          <nav className="navbar">
            <a className="brand" href="#accueil">
              <img src="/logo.jpeg" className="brand-logo" alt="Pouf Elite" />
              <span>Pouf Elite</span>
            </a>

            <div className="nav-links">
              <a href="#catalogue">Catalogue</a>
              <a href="#avantages">Avantages</a>
              <a href="#apropos">À propos</a>
              <a href="#contact">Contact</a>
            </div>

            <a className="nav-cta" href={whatsappLink} target="_blank" rel="noreferrer">
              Commander
            </a>
          </nav>
        </header>

        <main>
          <section className="hero" id="accueil">
            <div className="hero-content">
              <span className="eyebrow">Poufs premium au Maroc</span>

              <h1>
                Le confort élégant pour vos espaces <span>intérieurs et extérieurs</span>
              </h1>

              <p>
                Découvrez des poufs modernes, confortables et durables, conçus pour sublimer
                votre salon, chambre, terrasse, jardin, balcon ou espace détente. Avec Pouf Elite,
                profitez d’une assise élégante adaptée à tous vos moments de confort.
              </p>

              <div className="hero-actions">
                <a className="primary-button" href={whatsappLink} target="_blank" rel="noreferrer">
                  Commander sur WhatsApp
                </a>

                <a className="secondary-button" href="#catalogue">
                  Voir le catalogue
                </a>
              </div>

              <div className="hero-stats">
                <div>
                  <strong>Livraison</strong>
                  <span>Partout au Maroc</span>
                </div>

                <div>
                  <strong>Usage</strong>
                  <span>Intérieur & extérieur</span>
                </div>

                <div>
                  <strong>Style</strong>
                  <span>Design moderne</span>
                </div>
              </div>
            </div>

            <div className="hero-card">
              <div className="pouf-illustration">
                <div className="pouf-shadow"></div>
                <div className="pouf-body"></div>
                <div className="pouf-top"></div>
              </div>

              <div className="hero-badge">
                <strong>Pouf Elite</strong>
                <span>Catalogue automatique</span>
              </div>
            </div>
          </section>

          <section className="advantages" id="avantages">
            <div className="section-heading">
              <span>Pourquoi nous choisir ?</span>
              <h2>Des poufs pensés pour le confort quotidien</h2>
            </div>

            <div className="advantages-grid">
              <article>
                <div className="icon">✦</div>
                <h3>Confort premium</h3>
                <p>Un remplissage généreux pour une assise agréable et relaxante.</p>
              </article>

              <article>
                <div className="icon">◆</div>
                <h3>Intérieur & extérieur</h3>
                <p>
                  Des modèles adaptés au salon, à la chambre, au jardin, à la terrasse et au balcon.
                </p>
              </article>

              <article>
                <div className="icon">●</div>
                <h3>Livraison au Maroc</h3>
                <p>Commande simple via WhatsApp et livraison dans plusieurs villes marocaines.</p>
              </article>
            </div>
          </section>

          <section className="catalogue-section" id="catalogue">
            <div className="section-heading">
              <span>Notre catalogue</span>
              <h2>Choisissez votre modèle, matière, taille et couleur</h2>
              <p>
                Chaque panneau représente un modèle avec une matière. Sélectionnez une taille,
                puis cliquez sur une couleur.
              </p>
            </div>

            {catalogue.length === 0 ? (
                <div className="empty-catalogue">
                  <h3>Aucun produit trouvé</h3>
                  <p>
                    Ajoutez vos images dans : public/catalogue/Modèle/Matière/Taille/couleur.jpeg
                  </p>
                </div>
            ) : (
                <div className="catalogue-list">
                  {catalogue.flatMap((model) =>
                      model.materials.map((material) => {
                        const selectedSize = getSelectedSize(model, material);
                        const selectedColor = getSelectedColor(model, material, selectedSize);
                        const selectedSizeInfo = getSizeInfo(model, material, selectedSize);
                        const promoPrice = getPromoPrice(
                            selectedSizeInfo.prix,
                            selectedSizeInfo.promotion
                        );

                        return (
                            <article
                                className="catalogue-model"
                                key={`${model.id}-${material.id}`}
                            >
                              <div className="catalogue-model-header">
                                <span>Modèle / Matière</span>

                                <h3>
                                  {selectedSizeInfo.nom || model.name}
                                  <small>{selectedSizeInfo.matiere || material.name}</small>
                                </h3>
                              </div>

                              <div className="catalogue-model-body">
                                <div className="product-showcase">
                                  <div className="showcase-image-box">
                                    <div className="showcase-visual">
                                      <img
                                          key={selectedColor.image}
                                          src={selectedColor.image}
                                          alt={`${model.name} ${material.name} ${selectedSize.name} ${selectedColor.name}`}
                                          className="showcase-image"
                                          loading="lazy"
                                      />
                                    </div>

                                    <div className="catalogue-choice-row catalogue-size-row">
                                      <span>Taille</span>

                                      <div className="size-options">
                                        {material.sizes.map((size) => {
                                          const isActive = selectedSize.id === size.id;

                                          return (
                                              <button
                                                  type="button"
                                                  className={`size-option ${isActive ? 'active' : ''}`}
                                                  key={`${model.id}-${material.id}-${size.id}`}
                                                  onClick={() => selectSize(model, material, size)}
                                              >
                                                {size.name}
                                              </button>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    <div className="showcase-overlay">
                                      <div className="showcase-overlay-top">
                                        <span className="showcase-tag">Couleur sélectionnée</span>

                                        <h5>{selectedColor.name}</h5>

                                        <p>
                                          {selectedSizeInfo.nom || model.name} · Matière{' '}
                                          {selectedSizeInfo.matiere || material.name} · Taille{' '}
                                          {selectedSizeInfo.taille || selectedSize.name}
                                        </p>

                                        <div className="product-details">
                                          <span>Modèle : {selectedSizeInfo.nom || model.name}</span>
                                          <span>Matière : {selectedSizeInfo.matiere || material.name}</span>
                                          <span>Taille : {selectedSizeInfo.taille || selectedSize.name}</span>

                                          {selectedSizeInfo.hauteur && (
                                              <span>Hauteur : {selectedSizeInfo.hauteur}</span>
                                          )}

                                          {selectedSizeInfo.largeur && (
                                              <span>Largeur : {selectedSizeInfo.largeur}</span>
                                          )}
                                        </div>

                                        {!!selectedSizeInfo.prix && (
                                            <div className="product-price">
                                              {!!selectedSizeInfo.promotion && (
                                                  <span className="product-promo">
                                                    -{selectedSizeInfo.promotion}%
                                                  </span>
                                              )}

                                              {!!selectedSizeInfo.promotion && (
                                                  <span className="old-price">
                                                    {selectedSizeInfo.prix} DH
                                                  </span>
                                              )}

                                              <strong>
                                                {selectedSizeInfo.promotion
                                                    ? promoPrice
                                                    : selectedSizeInfo.prix}{' '}
                                                DH
                                              </strong>
                                            </div>
                                        )}
                                      </div>

                                      <a
                                          href={createProductWhatsappLink(
                                              model.name,
                                              material.name,
                                              selectedSize.name,
                                              selectedColor.name,
                                              selectedSizeInfo
                                          )}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="showcase-button"
                                      >
                                        Commander
                                      </a>
                                    </div>

                                    <div className="color-bubbles">
                                      {selectedSize.colors.map((color) => {
                                        const isActive = selectedColor.id === color.id;

                                        return (
                                            <button
                                                type="button"
                                                className={`color-bubble ${isActive ? 'active' : ''}`}
                                                key={`${model.id}-${material.id}-${selectedSize.id}-${color.id}`}
                                                onClick={() => selectColor(model, material, selectedSize, color)}
                                                aria-label={`Voir la couleur ${color.name}`}
                                                title={color.name}
                                            >
                                              <img
                                                  src={color.image}
                                                  alt={color.name}
                                                  loading="lazy"
                                              />
                                            </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </article>
                        );
                      })
                  )}
                </div>
            )}
          </section>

          <section className="about" id="apropos">
            <div className="about-content">
              <span>Pouf Elite Maroc</span>

              <h2>Une marque dédiée au confort, au style et à la qualité</h2>

              <p>
                Pouf Elite propose des poufs pratiques et élégants pour les maisons, terrasses,
                jardins, balcons et espaces de détente au Maroc. Notre objectif est simple :
                offrir une assise confortable, esthétique et accessible, adaptée aussi bien à
                l’intérieur qu’à l’extérieur.
              </p>
            </div>

            <div className="about-box">
              <h3>Commande rapide</h3>

              <p>
                Choisissez votre modèle, matière, taille et couleur, puis contactez-nous directement
                sur WhatsApp pour finaliser votre commande.
              </p>

              <a className="primary-button" href={whatsappLink} target="_blank" rel="noreferrer">
                Nous contacter
              </a>
            </div>
          </section>
        </main>

        <footer className="footer" id="contact">
          <div>
            <h2>Pouf Elite</h2>
            <p>Poufs confortables et modernes pour l’intérieur et l’extérieur au Maroc.</p>
          </div>

          <div className="footer-links">
            <a href={whatsappLink} target="_blank" rel="noreferrer">
              WhatsApp
            </a>

            <a href="#catalogue">Catalogue</a>
            <a href="#accueil">Accueil</a>
          </div>
        </footer>
      </div>
  );
}

export default App;